import {
  APP_INITIALIZER,
  ApplicationConfig,
  Provider,
  provideZoneChangeDetection,
  isDevMode,
  ErrorHandler,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  KeycloakEventTypeLegacy,
  KeycloakService,
} from 'keycloak-angular';
import { environment } from '@env';
import {
  FrontendCompanyService,
  FrontendRoutesService,
  FrontendUserService,
  KEYCLOAK_CONFIG,
  applyStoredImpersonationSessionToKeycloak,
  clearKeycloakTokenWithoutLogin,
  clearStoredImpersonationSession,
  hasImpersonationLaunchTokensInUrl,
  hasStoredImpersonationSession,
  keycloakInitOptionsWithImpersonationTokens,
  markStoredImpersonationSessionEnded,
  shouldBlockImpersonationFallback,
  tokenMatchesStoredImpersonationSession,
  updateStoredImpersonationTokens,
} from '@apruv-client/services';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ApiConfigInterceptor } from './shared/services/api-config.interceptor';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { jwtDecode } from 'jwt-decode';
import { provideNgxMask } from 'ngx-mask';
import * as Sentry from '@sentry/angular';
import { AuthBootstrapService } from './services/auth-bootstrap.service';

const debugLog = (...args: any[]): void => {
  if (!environment.production) {
    console.log(...args);
  }
};

// Custom ErrorHandler that wraps Sentry and also handles chunk load errors
// This is needed because Angular catches errors before they become unhandled rejections
class ChunkRecoveryErrorHandler implements ErrorHandler {
  private sentryHandler = Sentry.createErrorHandler();

  handleError(error: any): void {
    // Check if this is a chunk load error that should trigger recovery
    const chunkRecovery = (window as any).__APRUV_CHUNK_RECOVERY__;
    if (chunkRecovery) {
      const message = error?.message || error?.toString?.() || '';
      const errorName = error?.name || '';

      if (chunkRecovery.shouldHandleChunkError(message, errorName)) {
        debugLog(
          '🔄 Chunk error caught by Angular ErrorHandler, triggering recovery'
        );
        chunkRecovery.performChunkRecovery({
          source: 'angular:errorhandler',
          message,
          errorName,
          stack: error?.stack,
          userAgent: navigator.userAgent,
        });
        return; // Don't pass to Sentry since recovery will reload the page
      }
    }

    // For non-chunk errors, pass to Sentry
    this.sentryHandler.handleError(error);
  }
}

/**
 * Check if Web Crypto API is available for PKCE S256
 * Web Crypto requires a secure context (HTTPS or localhost)
 */
function isWebCryptoAvailable(): boolean {
  try {
    return !!(
      window.crypto &&
      window.crypto.subtle &&
      typeof window.crypto.subtle.digest === 'function'
    );
  } catch {
    return false;
  }
}

function shouldSkipKeycloakInit(path: string, url: string): boolean {
  return (
    path.startsWith('/access-denied') ||
    path.startsWith('/v1/verify/verification') ||
    path.startsWith('/v2/verify/verification') ||
    path.startsWith('/registration') ||
    path.startsWith('/review') ||
    path.startsWith('/test/verify-v2/') ||
    url.includes('verifyLink=true')
  );
}

function initializeKeycloak(
  keycloak: KeycloakService,
  router: Router,
  authBootstrapService: AuthBootstrapService
): () => Promise<boolean> {
  return async () => {
    debugLog('🔐 Keycloak initializer started');
    // Skip Keycloak initialization for public routes
    const path = window.location.pathname;
    const url = window.location.href;
    const usingImpersonationTokens =
      hasImpersonationLaunchTokensInUrl() || hasStoredImpersonationSession();

    if (shouldSkipKeycloakInit(path, url)) {
      debugLog('🔐 Keycloak initializer completed (skipped for public path)');
      return true;
    }

    if (shouldBlockImpersonationFallback()) {
      debugLog(
        '🔐 Keycloak initializer blocked after impersonation session ended'
      );
      await router.navigateByUrl('/access-denied?reason=impersonation-ended');
      return false;
    }

    debugLog(
      'Keycloak client ID from environment:',
      environment.keyCloakClientId
    );

    // Check if Web Crypto is available for PKCE
    const cryptoAvailable = isWebCryptoAvailable();
    if (!cryptoAvailable) {
      console.warn(
        '[KEYCLOAK] Web Crypto API not available - PKCE S256 disabled. Ensure HTTPS is used in production.'
      );
    }

    // Skip silent SSO on slow connections to avoid timeout + retry double round-trip.
    // On 3G, the silent SSO iframe takes 5-15s to timeout before falling back,
    // doubling the time before the app renders.
    const isSlowConnection = (() => {
      if ('connection' in navigator) {
        const effectiveType = (navigator as any).connection?.effectiveType;
        return (
          effectiveType === '2g' ||
          effectiveType === 'slow-2g' ||
          effectiveType === '3g'
        );
      }
      return false;
    })();

    if (isSlowConnection) {
      debugLog(
        '[KEYCLOAK] Slow connection detected, skipping silent SSO check'
      );
    }

    const keycloakConfig = {
      config: {
        url: environment.keyCloakUrl,
        realm: environment.keyCloakRealm,
        clientId: environment.keyCloakClientId,
      },
      initOptions: keycloakInitOptionsWithImpersonationTokens({
        onLoad: 'login-required' as const,
        checkLoginIframe: false,
        ...(cryptoAvailable ? { pkceMethod: 'S256' as const } : {}),
        responseMode: 'fragment' as const,
        // Only use silent SSO on fast connections
        ...(!isSlowConnection
          ? {
              silentCheckSsoRedirectUri:
                window.location.origin + '/assets/silent-check-sso.html',
            }
          : {}),
      }),
      enableBearerInterceptor: false,
      bearerExcludedUrls: [
        '/assets',
        '/public',
        'https://us1.api-bdc.net',
        'https://api-bdc.net',
      ],
    };

    let authenticated = false;
    try {
      authenticated = await keycloak.init(keycloakConfig);
    } catch (error: any) {
      // Check if this is a silent SSO timeout (common with third-party cookie blocking)
      const isSilentSsoTimeout =
        error?.message?.includes('3rd party check iframe') ||
        error?.message?.includes('Timeout');

      if (isSilentSsoTimeout && !usingImpersonationTokens) {
        console.warn(
          '[KEYCLOAK] Silent SSO check timed out (likely third-party cookies blocked). Retrying without silent SSO...'
        );
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Silent SSO timeout - retrying without silent SSO',
          level: 'warning',
        });

        // Retry without silent SSO
        try {
          const { silentCheckSsoRedirectUri, ...initWithoutSilentSSO } =
            keycloakConfig.initOptions as any;
          authenticated = await keycloak.init({
            ...keycloakConfig,
            initOptions: initWithoutSilentSSO,
          });
        } catch (retryError) {
          console.error('Keycloak initialization error (retry):', retryError);
          authenticated = false;
        }
      } else {
        console.error('Keycloak initialization error:', error);
        authenticated = false;
      }
    }

    // Bootstrap is handled by AuthBootstrapResolver on route activation
    // This prevents duplicate bootstrap calls and delays in initial render

    // Listen for re-login events to refetch data
    keycloak.keycloakEvents$.subscribe((event) => {
      if (event.type === KeycloakEventTypeLegacy.OnAuthSuccess) {
        (async () => {
          if (
            hasStoredImpersonationSession() ||
            shouldBlockImpersonationFallback()
          ) {
            debugLog(
              'Skipping auth-success bootstrap during impersonation session'
            );
            return;
          }

          debugLog('Auth success event triggered, fetching user data');
          // Set a flag to indicate this is a fresh login
          const isNewLogin = !sessionStorage.getItem('userAuthenticated');
          sessionStorage.setItem('userAuthenticated', 'true');

          // Sentry user context check
          try {
            // Always refresh Sentry user context on login for consistency
            const token = await keycloak.getToken();
            if (token) {
              const decodedToken: any = jwtDecode(token);
              Sentry.setUser({
                id: decodedToken?.sub || 'unknown',
                username: decodedToken?.preferred_username || 'unknown',
                email: decodedToken?.email || '',
              });
              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'User authenticated via Keycloak event',
                level: 'info',
              });
            }
          } catch (e) {
            console.error('Error setting Sentry user context:', e);
          }

          authBootstrapService.bootstrap('auth-success').catch((err) => {
            console.error(
              'Post-login bootstrap failed after auth success:',
              err
            );
          });
        })().catch((err) => console.error('Auth success handler failed:', err));
      }

      if (event.type === KeycloakEventTypeLegacy.OnAuthLogout) {
        debugLog('User logged out');
        if (hasStoredImpersonationSession()) {
          markStoredImpersonationSessionEnded();
          clearStoredImpersonationSession();
          clearKeycloakTokenWithoutLogin(keycloak.getKeycloakInstance());
          router
            .navigateByUrl('/access-denied?reason=impersonation-ended')
            .catch(() => {});
        } else {
          clearStoredImpersonationSession();
        }
        sessionStorage.removeItem('userAuthenticated');
        sessionStorage.removeItem('redirectAfterLogin');
        Sentry.setUser(null); // Clear user context on logout
      }

      if (event.type === KeycloakEventTypeLegacy.OnAuthError) {
        console.error('Auth error:', event.args);
        Sentry.captureMessage(
          `Keycloak auth error: ${JSON.stringify(event.args)}`
        );
      }
    });

    // Additional user initialization if authenticated
    if (usingImpersonationTokens || hasStoredImpersonationSession()) {
      const keycloakInstance = keycloak.getKeycloakInstance();
      const appliedImpersonationSession =
        applyStoredImpersonationSessionToKeycloak(keycloakInstance);
      if (
        !appliedImpersonationSession ||
        !tokenMatchesStoredImpersonationSession(keycloakInstance?.token)
      ) {
        markStoredImpersonationSessionEnded();
        clearStoredImpersonationSession();
        clearKeycloakTokenWithoutLogin(keycloakInstance);
        await router.navigateByUrl('/access-denied?reason=impersonation-ended');
        return false;
      }
      authenticated = true;

      updateStoredImpersonationTokens(
        keycloakInstance?.token,
        keycloakInstance?.idToken,
        keycloakInstance?.refreshToken
      );
    }

    debugLog('✅ Keycloak initializer completed');
    return true;
  };
}

// Provider for Keycloak Initialization
const KeycloakInitializerProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeKeycloak,
  multi: true,
  deps: [KeycloakService, Router, AuthBootstrapService],
};

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useClass: ChunkRecoveryErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        debugLog('🛡️ Sentry initializer started');
        debugLog('✅ Sentry initializer completed');
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(),
    provideAnimations(),
    provideNgxMask(),
    HttpClient,
    provideHttpClient(withInterceptorsFromDi()), // Provides HttpClient with interceptors
    KeycloakInitializerProvider,
    {
      provide: KEYCLOAK_CONFIG,
      useValue: {
        keyCloakUrl: environment.keyCloakUrl,
        keyCloakRealm: environment.keyCloakRealm,
        keyCloakClientId: environment.keyCloakClientId,
      },
    },
    { provide: HTTP_INTERCEPTORS, useClass: ApiConfigInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }, // Add Auth Interceptor
    KeycloakService,
    FrontendUserService,
    FrontendCompanyService,
    FrontendRoutesService,
    MessageService,
    ConfirmationService,
    DialogService,
  ],
};
